import { useMutation, useQuery } from "@tanstack/react-query"
import { BadgeCheckIcon, FilesIcon, Loader2Icon } from "lucide-react"
import { useState } from "react"
import {
  getReservationOfRightsStatus,
  ReservationOfRightsStatus,
  validateReservationOfRights,
} from "@/api/api"
import { NextStep } from "@/components/next-step"
import { PageLoader } from "@/components/page-loader"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { useCurrentToken } from "@/lib/use-current-token"
import { cn, parseError } from "@/lib/utils"

const ReservationPicker = ({
  status,
  token_id,
  onSuccess,
}: {
  status: ReservationOfRightsStatus
  token_id: string
  onSuccess: () => void
}) => {
  const [reason, setReason] = useState(status.reservation_of_rights_content)
  const [reservationOfRights, setReservationOfRights] = useState<
    boolean | null
  >(status.reservation_of_rights)

  let defaultValue: string | undefined
  if (status.reservation_of_rights !== null) {
    defaultValue = status.reservation_of_rights ? "yes" : "no"
  }

  const mutation = useMutation({
    mutationKey: ["validateReservationOfRights"],
    mutationFn: validateReservationOfRights,
    onSuccess: onSuccess,
  })

  const isVerified =
    status.status !== "unverified" && status.reservation_of_rights !== null

  if (isVerified) {
    return (
      <div className="flex flex-col items-center">
        <BadgeCheckIcon size={64} className="mb-6 text-primary" />

        <p className="pb-6 text-center">
          {status.reservation_of_rights ? (
            <span>
              Hemos la revisión de los anexos con tu reserva de derechos
              exitosamente: <br />
              <strong>{status.reservation_of_rights_content}</strong>
            </span>
          ) : (
            <span>Hemos registrado la revisión de los anexos.</span>
          )}
        </p>

        <NextStep />
      </div>
    )
  }

  return (
    <RadioGroup
      className="flex flex-col items-center justify-center gap-4"
      defaultValue={defaultValue}
      onValueChange={(value) => {
        mutation.reset()
        setReservationOfRights(value === "yes")
      }}
    >
      <Label
        className={cn(
          "flex w-full cursor-pointer flex-col items-center rounded-lg border p-6",
          "hover:bg-accent",
        )}
        htmlFor="no"
      >
        <div className="flex w-full items-start gap-3">
          <RadioGroupItem value="no" id="no" className="mt-1" />

          <div>
            <div className="text-lg">
              <strong>No</strong> me reservo los derechos
            </div>
            <p className="text-sm">
              Confirma que no se reserva los derechos y procede con el trámite
              en línea.
            </p>
          </div>
        </div>
      </Label>

      <Label
        className={cn(
          "flex w-full cursor-pointer flex-col items-center rounded-lg border p-6",
          "hover:bg-accent",
        )}
        htmlFor="yes"
      >
        <div className="flex w-full items-start gap-3">
          <RadioGroupItem value="yes" id="yes" className="mt-1" />

          <div>
            <div className="text-lg">
              <strong>Sí</strong> me reservo los derechos
            </div>

            <p className="text-sm">
              Si se reserva los derechos, debe indicar las razones y será
              derivado a realizar el trámite de manera presencial.
            </p>
          </div>
        </div>
      </Label>

      {reservationOfRights && (
        <Textarea
          className={cn("flex w-full flex-col items-center rounded-lg")}
          placeholder="Escribe aquí las razones de la reserva de derechos."
          autoFocus
          onChange={(e) => setReason(e.target.value)}
        >
          {reason}
        </Textarea>
      )}

      {mutation.isError && (
        <Alert variant="destructive">{parseError(mutation.error)}</Alert>
      )}

      {reservationOfRights !== null && (
        <Button
          className="w-full items-center lg:w-auto"
          disabled={mutation.isPending}
          onClick={() => {
            reservationOfRights !== null &&
              mutation.mutate({
                reservation_of_rights: reservationOfRights,
                reservation_of_rights_content: reason,
                token_id,
              })
          }}
        >
          {mutation.isPending ? (
            <>
              <Loader2Icon className="mb-1 mr-2 animate-spin" size={16} />
              <span>Guardando...</span>
            </>
          ) : (
            <span>Guardar</span>
          )}
        </Button>
      )}
    </RadioGroup>
  )
}

const ReservationOfRightsPage = () => {
  const { token_id, isReady } = useCurrentToken()

  const statusQuery = useQuery({
    queryKey: ["getReservationOfRightsStatus", { token_id }],
    queryFn: () => getReservationOfRightsStatus({ token_id }),
    enabled: isReady,
  })

  if (!isReady || statusQuery.isLoading) {
    return <PageLoader />
  }

  if (statusQuery.isError) {
    throw statusQuery.error
  }

  if (!statusQuery.isSuccess || !statusQuery.data) {
    throw new Error("Error inesperado")
  }

  const currentStatus = statusQuery.data

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12">
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col items-center justify-center md:flex-row">
            <FilesIcon className="mb-1.5 md:mr-3" />

            <h1 className="text-center text-xl md:text-2xl">Anexos</h1>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col items-center justify-center">
          <ReservationPicker
            status={currentStatus}
            token_id={token_id}
            onSuccess={statusQuery.refetch}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export { ReservationOfRightsPage }
